import React, { useEffect, useState, useRef } from 'react';
import { Users, Globe2, MapPin, School, Sparkles } from 'lucide-react';

/**
 * Animated count-up hook. Triggers when element enters viewport.
 * Handles numeric values with optional + suffix and K/M suffix.
 */
const useCountUp = (target, duration = 1600) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            setValue(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return [value, ref];
};

const formatNumber = (n, format) => {
  if (format === 'M') {
    return `${(n / 1_000_000).toFixed(1)}M`;
  }
  if (format === 'K') {
    return `${(n / 1000).toFixed(0)}K`;
  }
  return n.toLocaleString();
};

/* ---------------------------- IMPACT SECTION ---------------------------- */
const ImpactSection = () => {
  const [students, studentsRef] = useCountUp(1_600_000);
  const [countries, countriesRef] = useCountUp(10);
  const [states, statesRef] = useCountUp(20);
  const [schools, schoolsRef] = useCountUp(8000);

  return (
    <section className="relative mb-16" aria-label="nischals global impact">
      {/* Section header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-100 via-amber-100 to-emerald-100 border border-rose-200/60 mb-3">
          <Sparkles className="w-4 h-4" style={{ color: '#e4376f' }} />
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#e4376f' }}>
            Our Global Impact
          </span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Trusted across the world
        </h3>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
        {/* HERO CARD — Students Impacted (large, takes 6 cols on lg) */}
        <div
          ref={studentsRef}
          className="group relative sm:col-span-2 lg:col-span-6 lg:row-span-2 rounded-3xl overflow-hidden p-7 sm:p-9 text-white shadow-2xl shadow-rose-300/40 hover:-translate-y-1 transition-all min-h-[260px]"
          style={{ background: 'linear-gradient(135deg, #e4376f 0%, #c11d50 50%, #7c1c3f 100%)' }}
        >
          {/* Decorative pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="none">
            <defs>
              <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.5" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-amber-300/20 rounded-full blur-3xl" />

          <div className="relative h-full flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
                <Users className="w-6 h-6" strokeWidth={2.4} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Students Impacted</span>
            </div>

            <div>
              <div className="text-6xl sm:text-7xl lg:text-8xl font-extrabold tracking-tighter leading-none flex items-baseline gap-1">
                <span>{formatNumber(students, 'M')}</span>
                <span className="text-3xl sm:text-4xl lg:text-5xl text-amber-300 font-bold">+</span>
              </div>
              <p className="mt-3 text-base sm:text-lg text-white/90 max-w-md leading-relaxed">
                Young minds across the world have experienced the joy of hands-on learning through nischals.
              </p>
            </div>
          </div>
        </div>

        {/* Countries */}
        <div
          ref={countriesRef}
          className="group relative lg:col-span-3 rounded-3xl bg-white border border-slate-200 shadow-lg shadow-indigo-200/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-300/40 transition-all p-6 overflow-hidden min-h-[200px] flex flex-col justify-between"
        >
          {/* Decorative globe sphere */}
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-10" style={{ background: 'conic-gradient(from 45deg, #6366f1, #8b5cf6, #6366f1)' }} />
          <Globe2 className="absolute -right-4 -bottom-4 w-28 h-28 text-indigo-100" strokeWidth={1} />

          <div className="relative">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-md shadow-indigo-300/50 mb-3">
              <Globe2 className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-extrabold tracking-tight text-slate-900">{countries}</span>
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">+</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-1">Countries</p>
          </div>
          <p className="relative text-sm text-slate-600">Spanning Asia, Africa & the Middle East</p>
        </div>

        {/* Indian States */}
        <div
          ref={statesRef}
          className="group relative lg:col-span-3 rounded-3xl bg-white border border-slate-200 shadow-lg shadow-amber-200/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-300/40 transition-all p-6 overflow-hidden min-h-[200px] flex flex-col justify-between"
        >
          {/* Decorative map pin pattern */}
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-gradient-to-br from-amber-200 to-orange-200 opacity-30" />
          <MapPin className="absolute -right-2 -bottom-2 w-24 h-24 text-amber-100" strokeWidth={1} />

          <div className="relative">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-md shadow-amber-300/50 mb-3">
              <MapPin className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-extrabold tracking-tight text-slate-900">{states}</span>
              <span className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">+</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-1">Indian States</p>
          </div>
          <p className="relative text-sm text-slate-600">Reaching every region of India</p>
        </div>

        {/* Schools Worldwide — full width below on lg */}
        <div
          ref={schoolsRef}
          className="group relative sm:col-span-2 lg:col-span-6 rounded-3xl border border-slate-200 shadow-lg shadow-emerald-200/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-300/40 transition-all p-6 sm:p-7 overflow-hidden min-h-[150px]"
          style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f0fdfa 100%)' }}
        >
          {/* Decorative pattern */}
          <div className="absolute right-0 top-0 bottom-0 w-1/2 flex items-center justify-end pr-6 opacity-30 pointer-events-none">
            <School className="w-40 h-40 text-emerald-200" strokeWidth={1} />
          </div>

          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 h-full">
            <div>
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-md shadow-emerald-300/50 mb-3">
                <School className="w-5 h-5 text-white" strokeWidth={2.4} />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schools Worldwide</p>
              <p className="text-sm text-slate-600 mt-2 max-w-sm">
                Government, private &amp; international schools running nischals labs every day.
              </p>
            </div>
            <div className="flex items-baseline gap-1 sm:flex-shrink-0">
              <span className="text-6xl sm:text-7xl font-extrabold tracking-tight bg-gradient-to-br from-emerald-600 to-teal-700 bg-clip-text text-transparent">
                {formatNumber(schools).replace(',', ',')}
              </span>
              <span className="text-3xl font-bold text-emerald-500">+</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
